'use client';

import { useContext, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { SocketContext } from '@nx-frontend/shared/providers';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import {
  getDuplicateFolderStateFx,
  resetDuplicateFolderStateEvent,
  updateDuplicateFolderStateEvent,
} from '@nx-frontend/shared/store/duplicate-folder';
import {
  $menuState,
  deleteMenuItemEvent,
  updateMenuItemEvent,
  webnotesAddMenuItemEvent,
} from '@nx-frontend/shared/store/feature-menu-tree';
import { updateMenuItemParentSortOrderEvent } from '@nx-frontend/shared/store/internal-space-menu';
import { completeJobEvent, getJobFx } from '@nx-frontend/shared/store/jobs';
import { addNoteEvent, deleteNoteEvent, getNotesNoteFx, updateNoteEvent } from '@nx-frontend/shared/store/notes';
import { $orgUsage, loadOrgUsageEvent } from '@nx-frontend/shared/store/org-usage';
import { $user } from '@nx-frontend/shared/store/user';
import { MenuType } from '@nx-frontend/shared/types';
import { SidebarSocketEvents } from './socket-events';

/**
 * Listen on socket events and handle them to provide real-time updates
 */
export function useSocket() {
  const socket = useContext(SocketContext);
  const { toast } = useToast();
  const [currentWorkspace, user, menuState, orgUsage, currentOrganization] = useUnit([
    $currentWorkspace,
    $user,
    $menuState,
    $orgUsage,
    $currentOrganization,
  ]);
  const userId = user.id;

  const [
    addMenuItem,
    updateMenuItem,
    deleteMenuItem,
    addNote,
    updateNote,
    deleteNote,
    getNotesNote,
    updateMenuItemParentSortOrder,
    getJob,
    completeJob,
    getDuplicateFolderState,
    resetDuplicateFolderState,
    updateDuplicateFolderState,
    loadOrgUsage,
    openModal,
  ] = useUnit([
    webnotesAddMenuItemEvent,
    updateMenuItemEvent,
    deleteMenuItemEvent,
    addNoteEvent,
    updateNoteEvent,
    deleteNoteEvent,
    getNotesNoteFx,
    updateMenuItemParentSortOrderEvent,
    getJobFx,
    completeJobEvent,
    getDuplicateFolderStateFx,
    resetDuplicateFolderStateEvent,
    updateDuplicateFolderStateEvent,
    loadOrgUsageEvent,
    openModalEvent,
  ]);

  const t = useTranslations();

  useEffect(() => {
    if (socket) {
      SidebarSocketEvents.getInstance().init({
        listenSocketEvent: (event: string, callback) => {
          socket.on(event, callback);
        },
        workspaceId: currentWorkspace.globalId,
        userId,
        addMenuItem,
        updateMenuItem,
        deleteMenuItem,
        addNote,
        updateNote,
        deleteNote,
        getNotesNote,
        updateMenuItemParentSortOrder,
        webnotesMenuState: menuState[MenuType.WEBNOTES_MENU],
        toast,
        openModal,
        getJob,
        completeJob,
        getDuplicateFolderState,
        resetDuplicateFolderState,
        updateDuplicateFolderState,
        t,
      });
    }
    if (!orgUsage.isLoaded) {
      loadOrgUsage(currentOrganization);
    }
  }, [
    socket,
    currentWorkspace.globalId,
    menuState,
    addMenuItem,
    updateMenuItem,
    deleteMenuItem,
    addNote,
    updateNote,
    deleteNote,
    getNotesNote,
    updateMenuItemParentSortOrder,
    toast,
    userId,
    orgUsage,
    currentOrganization,
    loadOrgUsage,
    openModal,
    getJob,
    completeJob,
    getDuplicateFolderState,
    resetDuplicateFolderState,
    updateDuplicateFolderState,
    t,
  ]);
}
